import { Injectable, signal } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { HttpCacheService } from '@app/core/http/http-cache.service';
import { __ } from '@app/shared/functions/object.functions';

export type ContentLocalePreference = 'auto' | 'source' | string;

@Injectable({ providedIn: 'root' })
export class ContentLocaleService {
  private readonly preferenceState = signal<ContentLocalePreference>(this.readPreference());
  private readonly preferenceChanges = new ReplaySubject<ContentLocalePreference>(1);

  readonly preferenceSignal = this.preferenceState.asReadonly();
  readonly preference$ = this.preferenceChanges.asObservable();

  constructor(private readonly httpCacheService: HttpCacheService) {
    // Preserve the old BehaviorSubject compatibility contract for RxJS consumers
    // without making BehaviorSubject the source of truth. ReplaySubject keeps direct
    // `new ContentLocaleService(...)` tests independent from an Angular injection context.
    this.preferenceChanges.next(this.preferenceState());
  }

  get preference(): ContentLocalePreference {
    return this.preferenceState();
  }

  setPreference(value: ContentLocalePreference): void {
    const normalized = this.normalizePreference(value);
    if (normalized !== this.preferenceState()) {
      // Responses are cached under `urlWithParams`, and in `auto` mode the interceptor
      // adds no `locale` param — so the entries written under the previous preference
      // would simply be replayed for up to their whole TTL instead of being refetched.
      // Drop them BEFORE announcing the change, so everything that reacts to
      // `preference$` reads from the API and not from the pre-switch cache.
      this.httpCacheService.cleanCache();
    }

    this.preferenceState.set(normalized);
    this.preferenceChanges.next(normalized);
    this.writePreference(normalized);
  }

  getExplicitLocale(): string | null {
    return this.preference === 'auto' ? null : this.preference;
  }

  private normalizePreference(value: ContentLocalePreference): ContentLocalePreference {
    if (value === 'auto' || value === 'source') {
      return value;
    }
    return this.normalizeLocale(value) ?? 'auto';
  }

  private normalizeLocale(value: string | null | undefined): string | null {
    const candidate = value?.trim().replace(/_/g, '-');
    if (!candidate) {
      return null;
    }
    try {
      return Intl.getCanonicalLocales(candidate)[0] ?? null;
    } catch {
      return null;
    }
  }

  private readPreference(): ContentLocalePreference {
    // Reading window.localStorage throws SecurityError outright when the embedding host
    // blocks storage access, and this service is constructed while HttpService is being
    // built — an escaping error would take down every request in the plugin iframes.
    if (!__.storageAvailable('localStorage')) {
      return 'auto';
    }
    try {
      return this.normalizePreference(localStorage.getItem(this.storageKey()) ?? 'auto');
    } catch {
      return 'auto';
    }
  }

  private writePreference(value: ContentLocalePreference): void {
    if (!__.storageAvailable('localStorage')) {
      return;
    }
    try {
      localStorage.setItem(this.storageKey(), value);
    } catch {
      // Safari private mode reports storage as available and then throws QuotaExceededError.
    }
  }

  private storageKey(): string {
    const host = typeof location === 'undefined' ? 'default' : location.host || 'default';
    return `hypecast.content-locale.${host}`;
  }

}
